import {ThemeColorTextPath} from 'platform/foundation';

import {Nullish} from 'shared/web';

import {LineHeight} from '../utils/lineHeightToValue';
import {ColGroupDef} from './AgGridTypes';
import {Filters} from './Filters';

export type BigDecimal = string;
type Currency = string;

export type Money = {
  amount: BigDecimal;
  currency: Currency;
};

export type Enum = {
  key: string;
  label: string;
};

export type Entity = {
  id: string;
  label: string;
};

export type QuickFilter = {value: string; label: string};

export type QuickFilters = {
  quickFilters: QuickFilter[];
};

export type ChoiceFilterOptions = Array<{label: string; value: string}>;

export type TextFilterType = {
  type: 'TEXT';
  isDisabled: boolean;
} & QuickFilters;

export type SwitchFilterType = {
  type: 'SWITCH';
  isDisabled: boolean;
} & QuickFilters;

export type SuggestFiterType = {
  type: 'MULTI_CHOICE_ASYNC';
  suggestId: string;
  isDisabled: boolean;
} & QuickFilters;

export type SliderFilterType = {
  type: 'SLIDER' | 'SLIDER_RANGE';
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  unit: string | null;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  decimalsAllowed: boolean;
  inputsVisible: boolean;
  isDisabled: boolean;
} & QuickFilters;

export type RowActionsResponseBody = {
  primary: string[];
  secondary: string[];
  rowClick: string | null;
};

export type RangeFilterType = {
  type: 'RANGE';
  isDisabled: boolean;
} & QuickFilters;

export type DateRangeTimeFilterType = {
  type: 'DATE_TIME_RANGE';
  min: string | null;
  max: string | null;
  relativeDates: RelativeDateResponseBody[];
  isDisabled: boolean;
  relativeInputs: Array<RelativeInputType>;
} & QuickFilters;

export type RelativeInputType = {
  label: string;
  value: string;
} & QuickFilters;

export type ChoiceFilterType = {
  type: 'MULTI_CHOICE' | 'CHIPS' | 'CHECKBOX';
  options?: FilterOptionResponseBody[];
  isDisabled: boolean;
  multiple: boolean;
} & QuickFilters;

export type FilterOptionResponseBody = {
  key: string;
  label: string;
};

export type DateFilterType = {
  type: 'DATE' | 'DATE_RANGE';
  mode: 'DAY';
  min: string | null;
  max: string | null;
  relativeDates: RelativeDateResponseBody[];
  isDisabled: boolean;
  relativeInputs: Array<RelativeInputType>;
} & QuickFilters;

export type Style = {
  textColor: ThemeColorTextPath | null;
  icon?: {
    type: string;
    color: string;
  } | null;
};

export type CellsV5 =
  | {
      value: string | string[];
      style: Style;
    }
  | {
      value: Entity | Entity[];
      style: Style;
    }
  | {
      value: Enum | Enum[];
      style: Style;
    }
  | {
      value: Money | Money[];
      style: Style;
    }
  | {
      value: string | string[];
      style: Style;
    }
  | {
      value: string | string[];
      style: Style;
    }
  | {
      value: boolean | boolean[];
      style: Style;
    }
  | {
      value: number | number[];
      style: Style;
    }
  | {
      url: string | string[];
      style: Style;
    };

export type RowCountRequestBody = {
  filters: {
    [key: string]: string | string[] | boolean;
  };
};

export type RowCountResponse = {
  totalRows: number;
};

export type SuggestionsOptions = Array<{label: string; value: string}>;

export type GetFilterSchemaParams = {
  externalFilterRecordId: string | Nullish;
  externalFilterResourceId: string | Nullish;
};

export type GetDefinitionParams = {
  loadChoiceFilterOptions?: boolean;
};

export type GetSuggestionLabelsParams = {
  suggestId: string;
  keys: string[];
};

export type GetSuggestionOptionsParams = {
  suggestId: string;
  inputValue: string | null;
  limit: number;
};

export type SuggestionsResponseBody = {
  suggestions: {
    key: string;
    label: string;
  }[];
};

export type DataV5ResponseBody = {
  rows: RowV5ResponseBody[];
};

export type DataQueryFiltersValue =
  | string
  | Array<string>
  | {from?: string | undefined; to?: string | undefined};

export type DataQueryRequestBody = {
  offset: number;
  limit: number;
  filters: {
    [key: string]: DataQueryFiltersValue;
  };
  order: OrderByRequestBody[];
};

export type QueryIdRequestBody = {
  smartSearch: string | null;
  filters: {
    [key: string]: DataQueryFiltersValue;
  };
  order: OrderByRequestBody[];
};

export type SharePresetRequestBody = {
  gridSettings: boolean;
  columnSettings: boolean;
  filters: boolean;
  order: boolean;
};

export type SharePresetResponseBody = {
  sharedPresetId: string;
};

export type SharePresetToUseRequestBody = {
  userIds: string[];
};

export type SharePresetToUseResponseBody = void;

export type SummaryRowResponseBody = {
  [key: string]: CellsV5;
};

export type RowV5ResponseBody = {
  id: string;
  cells: {
    [key: string]: CellsV5;
  };
  actions: RowActionsResponseBody;
  rowNumber: number;
};

export type ColumnSettingsResponseBody = {
  columnKey: string;
  visible: boolean;
  width?: number;
  pinned: ('left' | 'right') | null;
};

export type OrderByResponseBody = {
  columnKey: string;
  order: 'ASC' | 'DESC';
};

export type BasePresetResponseBody = {
  id: string;
  title: string;
  description: string | null;
  type: 'VIRTUAL' | 'CUSTOM';
  columns: ColumnSettingsResponseBody[];
  filters: {
    [key: string]: string;
  };
  order: OrderByResponseBody[];
};

export type CustomPresetResponseBody = BasePresetResponseBody & {
  active: boolean;
  editable: boolean;
  deletable: boolean;
};

export type VirtualPresetResponseBody = BasePresetResponseBody;

export type ColumnStateRequestBody = {
  columnKey: string;
  visible: boolean;
  width: number;
  pinned: ('left' | 'right') | null;
};

export type OrderByRequestBody = {
  columnKey: string;
  order: 'ASC' | 'DESC';
};

export type SettingsResponseBody = {
  headerHeight: 'COMPACT' | 'SHORT' | 'DEFAULT' | 'TALL' | 'THUMBNAIL';
  valueWrapMode: 'TRUNCATE' | 'WRAP';
  horizontalSeparatorsVisible: boolean;
  itemsPerPage: number;
  keyboardNavigationEnabled: boolean;
  rowHeight: 'COMPACT' | 'SHORT' | 'DEFAULT' | 'TALL' | 'THUMBNAIL';
  columnGroupsVisible: boolean;
  stripedRows: boolean;
  summaryRowVisible: boolean;
  verticalSeparatorsVisible: boolean;
};

export type PresetRequestBody = {
  title: string;
  description: string | null;
  columns: ColumnStateRequestBody[];
  filters: Record<
    string,
    | string
    | Array<string>
    | boolean
    | {
        from?: string;
        to?: string;
      }
  >;
  order: OrderByRequestBody[];
  instance: string | Nullish;
};

export type GetSmartSearchResponse = {
  queries: {
    query: string;
  }[];
};

export type TransformedDefinitionApiResponse = Omit<DefinitionResponseBody, 'groups'> & {
  groups: Record<string, ColGroupDef>;
};

export type ColumnRendererResponseBody = {
  type:
    | 'PLAIN'
    | 'METATEXT'
    | 'LINK'
    | 'FLAG'
    | 'ICON'
    | 'DATE'
    | 'DATE_TIME'
    | 'CHECKBOX'
    | 'THUMBNAIL'
    | 'INTEGER'
    | 'FILE_SIZE'
    | 'TAG_VALUE';
};
export type DecimalRendererResponseBody = {
  type: 'DECIMAL';
  decimalPlaces: number;
};
export type MoneyRendererResponseBody = {
  type: 'MONEY' | 'MONEY_WITHOUT_DECIMAL';
  decimalPlaces: number;
};
export type QuickFilterResponseBody = {
  value: 'QUICK_FILTER_EMPTY' | 'QUICK_FILTER_FILLED';
  label: string;
};
export type RelativeDateResponseBody = {
  value: 'TODAY' | 'YESTERDAY' | 'TOMORROW';
  label: string;
};
export type RelativeDateRangeResponseBody = {
  value:
    | 'TODAY'
    | 'YESTERDAY'
    | 'TOMORROW'
    | 'THIS_WEEK'
    | 'LAST_WEEK'
    | 'NEXT_WEEK'
    | 'THIS_MONTH'
    | 'LAST_MONTH'
    | 'NEXT_MONTH'
    | 'THIS_YEAR'
    | 'LAST_YEAR'
    | 'NEXT_YEAR';
  label: string;
};
export type RelativeInputResponseBody = {
  value: 'LAST_N_DAYS' | 'NEXT_N_DAYS';
  label: string;
};
export type ColumnFilterResponseBody =
  | (
      | ({
          type: 'TEXT' | 'SWITCH';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
        } | null)
      | ({
          type: 'DATE';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          mode: 'DAY';
          min: string | null;
          max: string | null;
          relativeDates: RelativeDateResponseBody[];
        } | null)
      | ({
          type: 'DATE_RANGE';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          mode: 'DAY';
          min: string | null;
          max: string | null;
          relativeDates: RelativeDateRangeResponseBody[];
          relativeInputs: RelativeInputResponseBody[];
        } | null)
      | ({
          type: 'DATE_TIME_RANGE';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          min: string | null;
          max: string | null;
          relativeDates: RelativeDateRangeResponseBody[];
          relativeInputs: RelativeInputResponseBody[];
        } | null)
      | ({
          type: 'MULTI_CHOICE_ASYNC';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          suggestId: string;
        } | null)
      | ({
          type: 'MULTI_CHOICE';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          options?: FilterOptionResponseBody[];
          multiple: boolean;
        } | null)
      | ({
          type: 'CHIPS' | 'CHECKBOX';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          options?: FilterOptionResponseBody[];
        } | null)
      | ({
          type: 'SLIDER' | 'SLIDER_RANGE';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          min: number;
          max: number;
          minOpen: boolean;
          maxOpen: boolean;
          unit: string | null;
          stepSize: number | null;
          steps: number[];
          markerSize: number | null;
          markers: number[];
          decimalsAllowed: boolean;
          inputsVisible: boolean;
        } | null)
      | ({
          type: 'FILE_SIZE_SLIDER';
          isDisabled: boolean;
          quickFilters: QuickFilterResponseBody[];
          min: number;
          max: number;
          minOpen: boolean;
          maxOpen: boolean;
          stepSize: number | null;
          steps: number[];
          markerSize: number | null;
          markers: number[];
          inputsVisible: boolean;
        } | null)
      | ({
          type: 'RANGE';
          isDisabled: boolean;
        } | null)
    )
  | null;

export type ColumnResponseBody = {
  key: string;
  type:
    | 'TEXT'
    | 'TIME_INTERVAL'
    | 'ENTITY'
    | 'ENUM'
    | 'MONEY'
    | 'DATE'
    | 'DATE_TIME'
    | 'BOOLEAN'
    | 'IMAGE'
    | 'INTEGER'
    | 'FLOAT'
    | 'CODE_LIST'
    | 'TAG_VALUE'
    | 'FILE';
  title: string;
  renderer: ColumnRendererResponseBody | DecimalRendererResponseBody | MoneyRendererResponseBody;
  group: string | null;
  filter: ColumnFilterResponseBody;
  sortable: boolean;
  smartSearch: boolean;
  isArray: boolean;
  hidden: boolean;
};

export type EntityColumnResponseBody = ColumnResponseBody & {
  resourceId: string;
};

export type EnumColumnResponseBody = ColumnResponseBody & {
  enumType: string;
};

export type TagValueColumnResponseBody = ColumnResponseBody & {
  tagId: string;
};

export type ColumnGroupResponseBody = {
  key: string;
  title: string;
};

export type ActionResponseBody = {
  key: string;
  title: string;
  description: string;
  variant: string | null;
  icon: string | null;
  single: boolean;
  bulk: boolean;
  isLink: boolean;
};

export type BehaviorResponseBody = {
  settingsToolPanelVisible: boolean;
  columnToolPanelVisible: boolean;
  filterToolPanelVisible: boolean;
  actionColumnEnabled: boolean;
  rangeSelectAllowed: boolean;
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  wrapperStyle: 'CARD' | 'INLINE';
  paginationEnabled: boolean;
  clipboardExportAllowed: boolean;
  columnMenuEnabled: boolean;
  columnMovingAllowed: boolean;
  columnPinningAllowed: boolean;
  columnSortMode: 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE';
  contextMenuEnabled: boolean;
  columnResizingAllowed: boolean;
  fitToScreenAllowed: boolean;
  presetsEnabled: boolean;
  smartSearchEnabled: boolean;
  filterMode: 'NATIVE' | 'CUSTOM';
  columnHidingAllowed: boolean;
  columnAutoSizingAllowed: boolean;
  statusBarVisible: boolean;
  summaryRowEnabled: boolean;
  globalActionsEnabled: boolean;
};

export type DataGridBehavior = DefinitionResponseBody['behavior'];
export type DataGridBehaviorOverrides = Partial<DefinitionResponseBody['behavior']>;

export type DefinitionResponseBody = {
  columns: (
    | ColumnResponseBody
    | EntityColumnResponseBody
    | EnumColumnResponseBody
    | TagValueColumnResponseBody
  )[];
  columnGroups: ColumnGroupResponseBody[];
  actions: ActionResponseBody[];
  behavior: BehaviorResponseBody;
};

export type GetPresetsApiResponse = CustomPresetResponseBody[];
export type GetVirtualPresetApiResponse = VirtualPresetResponseBody;

export type GetDataIdsByQuery = {
  id: string;
  rowNumber: number;
};

export type ValueWrapMode = 'TRUNCATE' | 'WRAP';

export type BaseGridSettings = {
  valueWrapMode: ValueWrapMode;
  horizontalSeparatorsVisible: boolean;
  itemsPerPage: number;
  keyboardNavigationEnabled: boolean;
  columnGroupsVisible: boolean;
  stripedRows: boolean;
  summaryRowVisible: boolean;
  verticalSeparatorsVisible: boolean;
};

export type BeGridSettings = BaseGridSettings & {
  headerHeight: LineHeight;
  rowHeight: LineHeight;
};

export type FeGridSettings = BaseGridSettings & {
  headerHeight: LineHeight;
  rowHeight: LineHeight;
};

export type ColumnsSetting = {
  columnKey: string;
  visible: boolean;
  width: number;
  pinned: 'LEFT' | 'RIGHT' | null;
};

export type VirtualPreset = {
  gridSettings: FeGridSettings;
  columnsSettings: ColumnsSetting[];
  dataQueryId: string;
};

export type BeVirtualPreset = {
  gridSettings: BeGridSettings;
  columnsSettings: ColumnsSetting[];
  dataQueryId: string;
};

export type Preset = {
  id: string;
  title: string;
  description: string;
  dataQueryId: string;
  isActive: boolean;
};

export type ActivePreset = VirtualPreset | PresetDetail;

export type PresetDetail = Preset & {
  gridSettings: FeGridSettings;
  columnsSettings: ColumnsSetting[];
};

export type GetPresetsResponse = (Preset & {isActive: boolean})[];

export type GetPresetRequest = {presetId: string};
export type GetPresetResponse = PresetDetail;

export type PostPresetRequest = PresetDetail;
export type PostPresetResponse = {presetId: string};

export type PutPresetRequest = {
  dataQueryId: string;
  gridSettings: FeGridSettings;
  columnsSettings: ColumnsSetting[];
};
export type PutPresetResponse = {presetId: string};

export type PutPresetRenameRequest = {
  title: string;
  description: string;
};
export type PutPresetRenameResponse = {presetId: string};

export type OrderType = 'ASC' | 'DESC';
export type OrderTypeLowercase = 'asc' | 'desc';
export type Filter = Record<string, string>;
export type Order = {
  columnKey: string;
  order: OrderType;
};

export type GetDataQueryRequest = string;
export type GetDataQueryResponse = {
  id: string;
  smartSearch: string | null;
  filters: Filters;
  order: Order[];
};

export type ExportFileType = 'excel' | 'csv' | 'pdf';
export type ExportDecimalDelimiter = 'comma' | 'dot';
export type ExportColumnDelimiter = 'comma' | 'semicolon';
export type AsyncExportDataRequestBody = {
  columns: string[];
  dataQueryId: string;
  fileType: ExportFileType;
  decimalDelimiter?: ExportDecimalDelimiter | null;
  columnDelimiter?: ExportColumnDelimiter | null;
};

export type DataExportAcceptedResponseBody = {
  exportId: string;
};
